// areaSpecialTools.js — Bespoke draw interactions for AREA special rules.
// AREA2 (polygon + LAA point), AREA26 (paired-polygon), AREA27 (line + arc side)

import { DrawRules } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import { LineString, Polygon } from 'ol/geom';
import { toLonLat } from 'ol/proj';
import { registerDrawInteraction } from '../customDrawInteractions';
import { createFixedPointCollector } from './fixedPointCollector';
import { createMultiPointCollector } from './multiPointCollector';
import { PREVIEW_DOT_AMBER, PREVIEW_LINE_BLUE_DASHED, PREVIEW_LINE_AMBER_DASHED, PREVIEW_LINE_GREEN_DASHED } from './drawPreviewStyles';

// ── AREA2: free polygon then 1 extra LAA anchor point ─────────────────────────
// ponytail: two-phase collector — first phase runs a free-polygon OL Draw,
// second phase collects one more click for the LAA point. Doesn't sit on top of
// multiPointCollector.js (its phase 1 IS OL's own `Draw` interaction, not a sequential-click
// collector this app owns the loop for) — but still gets Escape-to-cancel during phase 2,
// consistent with every other migrated tool (ticket 04's "verify current per-file behavior
// first" found this file's AREA2 had no cancel path for its LAA-point phase before this).
import Draw from 'ol/interaction/Draw';
import { fromLonLat } from 'ol/proj';
import GeoJSON from 'ol/format/GeoJSON';
const FORMAT = new GeoJSON({ featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' });

registerDrawInteraction(DrawRules.AREA2, (map, opts) => {
    let phase = 1; // 1 = drawing polygon, 2 = placing LAA point
    let polygonCoords = null;
    let placedCount = 0;

    const previewSource = new VectorSource();
    const previewLayer = new VectorLayer({ source: previewSource, zIndex: 10,
        style: [PREVIEW_LINE_AMBER_DASHED, PREVIEW_DOT_AMBER],
    });
    map.addLayer(previewLayer);

    const drawIA = new Draw({
        source: new VectorSource(), // throwaway
        type: 'Polygon',
        condition: (e) => e.originalEvent.button === 0,
        stopClick: true,
    });

    drawIA.on('drawend', (e) => {
        const gf = FORMAT.writeFeatureObject(e.feature, { featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' });
        polygonCoords = gf.geometry.coordinates[0]; // ring
        phase = 2;
        map.removeInteraction(drawIA);
        // Show polygon outline in preview
        previewSource.clear();
        const pf = new Feature(new Polygon([polygonCoords.map((c) => fromLonLat(c))]));
        previewSource.addFeature(pf);
    });

    map.addInteraction(drawIA);

    const onLaaClick = (e) => {
        if (phase !== 2 || e.originalEvent.button !== 0) return;
        const lonLat = toLonLat(e.coordinate);
        const laaCoord = [lonLat[0], lonLat[1]];
        cleanup();
        opts.onFinish([...polygonCoords, laaCoord]);
    };

    // Escape cancels whichever phase is active: phase 1 aborts OL's own in-progress polygon
    // sketch (Draw's native behavior — it already handles Escape internally for the sketch
    // itself, but doesn't tear down THIS interaction/its listeners, so this still needs its own
    // handler to fully cancel); phase 2 backs out of the LAA-point click without finishing.
    const onKeyDown = (e) => {
        if (e.key === 'Escape') cleanup();
    };

    map.on('click', onLaaClick);
    document.addEventListener('keydown', onKeyDown);
    placedCount = 0;

    function cleanup() {
        map.un('click', onLaaClick);
        document.removeEventListener('keydown', onKeyDown);
        map.removeInteraction(drawIA);
        map.removeLayer(previewLayer);
    }

    return {
        interaction: drawIA,
        getPlacedCount: () => placedCount,
        destroy: cleanup,
    };
});

// ── AREA26: paired polygon — even count ≥ 6, first half = zone1, second = zone2 ──
registerDrawInteraction(DrawRules.AREA26, (map, opts) => createMultiPointCollector(map, {
    minCount: 6,
    maxCount: Infinity,
    onFinish: opts.onFinish,
    canFinish: (n) => n >= 6 && n % 2 === 0,
    // Every point is a plain zone-boundary vertex — the zone1/zone2 split is purely positional
    // (first half vs second half of the final count, which isn't knowable mid-placement), not a
    // distinct semantic role like a tip or width point — so all points stay 'anchor'; the
    // zone1/zone2 visual split below is handled via styleResolver on the LINE features instead.
    roleForIndex: () => 'anchor',
    styleResolver: (f) => (f.get('_style') === 'zone2' ? PREVIEW_LINE_GREEN_DASHED : PREVIEW_LINE_BLUE_DASHED),
    onSketchUpdate: ({ previewSource, collected3857 }) => {
        const half = Math.floor(collected3857.length / 2);
        const z1 = collected3857.slice(0, half);
        const z2 = collected3857.slice(half);
        if (z1.length >= 2) {
            previewSource.addFeature(new Feature(new LineString(z1)));
        }
        if (z2.length >= 2) {
            const lf = new Feature(new LineString(z2));
            lf.set('_style', 'zone2');
            previewSource.addFeature(lf);
        }
    },
}));

// ── AREA27: 3-pt (P1 tip, P2 spine end, P3 arc side + diameter) ──────────────
registerDrawInteraction(DrawRules.AREA27, (map, opts) =>
    createFixedPointCollector(map, { targetCount: 3, onFinish: opts.onFinish }));

